/**
 * Role-based permission utility functions
 * 
 * Roles hierarchy (each includes privileges of previous):
 * 1. Viewer - Read-only access to analytics
 * 2. Editor - Viewer + can edit QR codes
 * 3. Manager - Editor + can manage users and assign roles
 * 4. Admin - Manager + can access payment/billing settings
 * 5. Primary - All Admin privileges + account owner designation
 */

// Role constants
export const ROLES = {
  VIEWER: 'viewer',
  EDITOR: 'editor',
  MANAGER: 'manager',
  ADMIN: 'admin',
  PRIMARY: 'primary'
};

// Role hierarchy for comparison (higher index = more privileges)
const ROLE_HIERARCHY = [
  ROLES.VIEWER,
  ROLES.EDITOR,
  ROLES.MANAGER,
  ROLES.ADMIN,
  ROLES.PRIMARY
];

/**
 * Get permissions for a specific role from config
 * @param {Object} config - Auth config object containing roles definition
 * @param {string} roleId - Role identifier (viewer, editor, manager, admin, primary)
 * @returns {Object} Permissions object { analytics, edit_qr, manage_users, admin }
 */
export const getRolePermissions = (config, roleId) => {
  if (!config?.roles || !roleId) {
    return { analytics: false, edit_qr: false, manage_users: false, admin: false };
  }
  
  return config.roles[roleId] || { analytics: false, edit_qr: false, manage_users: false, admin: false };
};

/**
 * Check if a user has a specific permission in the current organization
 * @param {Object} userOrgData - User's organization data from subcollection
 * @param {Object} config - Auth config object containing roles definition
 * @param {string} permission - Permission to check (analytics, edit_qr, manage_users, admin)
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (userOrgData, config, permission) => {
  if (!userOrgData || !config?.roles || !permission) {
    return false;
  }

  // Primary users have all permissions
  if (userOrgData.is_primary) {
    return true;
  }

  const rolePermissions = getRolePermissions(config, userOrgData.role);
  return rolePermissions[permission] === true;
};

/**
 * Check if user can view analytics
 * @param {Object} userOrgData - User's organization data
 * @param {Object} config - Auth config object
 * @returns {boolean}
 */
export const canViewAnalytics = (userOrgData, config) => {
  return hasPermission(userOrgData, config, 'analytics');
};

/**
 * Check if user can edit QR codes
 * @param {Object} userOrgData - User's organization data
 * @param {Object} config - Auth config object
 * @returns {boolean}
 */
export const canEditQR = (userOrgData, config) => {
  return hasPermission(userOrgData, config, 'edit_qr');
};

/**
 * Check if user can manage other users (invite, remove, change roles)
 * @param {Object} userOrgData - User's organization data
 * @param {Object} config - Auth config object
 * @returns {boolean}
 */
export const canManageUsers = (userOrgData, config) => {
  return hasPermission(userOrgData, config, 'manage_users');
};

/**
 * Check if user has admin privileges (billing, org settings)
 * @param {Object} userOrgData - User's organization data
 * @param {Object} config - Auth config object
 * @returns {boolean}
 */
export const isAdmin = (userOrgData, config) => {
  return hasPermission(userOrgData, config, 'admin');
};

/**
 * Check if user is the primary account holder
 * @param {Object} userOrgData - User's organization data
 * @returns {boolean}
 */
export const isPrimary = (userOrgData) => {
  return userOrgData?.is_primary === true;
};

/**
 * Compare two roles to determine which has higher privileges
 * @param {string} role1 - First role to compare
 * @param {string} role2 - Second role to compare
 * @returns {number} -1 if role1 < role2, 0 if equal, 1 if role1 > role2
 */
export const compareRoles = (role1, role2) => {
  const index1 = ROLE_HIERARCHY.indexOf(role1);
  const index2 = ROLE_HIERARCHY.indexOf(role2);
  
  if (index1 < index2) return -1;
  if (index1 > index2) return 1;
  return 0;
};

/**
 * Check if a user can modify another user's role
 * Rules:
 * - Only Managers and above can change roles
 * - Cannot change role of Primary user
 * - Cannot assign a role higher than your own
 * - Primary can do anything
 * @param {Object} currentUserOrgData - Current user's org data
 * @param {Object} targetUserOrgData - Target user's org data
 * @param {string} newRole - New role being assigned
 * @returns {boolean}
 */
export const canChangeUserRole = (currentUserOrgData, targetUserOrgData, newRole) => {
  if (!currentUserOrgData || !targetUserOrgData) {
    return false;
  }

  // Cannot change Primary user's role (unless you are Primary)
  if (targetUserOrgData.is_primary && !currentUserOrgData.is_primary) {
    return false;
  }

  // Primary can change anyone's role
  if (currentUserOrgData.is_primary) {
    return true;
  }

  // Must be at least Manager to change roles
  const currentRole = currentUserOrgData.role;
  if (compareRoles(currentRole, ROLES.MANAGER) < 0) {
    return false;
  }

  // Cannot assign a role higher than your own
  if (compareRoles(newRole, currentRole) > 0) {
    return false;
  }

  // Cannot modify someone with higher role (but can modify equal role)
  if (compareRoles(targetUserOrgData.role, currentRole) > 0) {
    return false;
  }

  return true;
};

/**
 * Check if a user can remove another user from the organization
 * Rules:
 * - Only Managers and above can remove users
 * - Cannot remove Primary user
 * - Cannot remove users with equal or higher role
 * - Primary can remove anyone (except themselves, handled in UI)
 * @param {Object} currentUserOrgData - Current user's org data
 * @param {Object} targetUserOrgData - Target user's org data
 * @returns {boolean}
 */
export const canRemoveUser = (currentUserOrgData, targetUserOrgData) => {
  if (!currentUserOrgData || !targetUserOrgData) {
    return false;
  }

  // Cannot remove Primary user
  if (targetUserOrgData.is_primary) {
    return false;
  }

  // Primary can remove anyone (UI should prevent self-removal)
  if (currentUserOrgData.is_primary) {
    return true;
  }

  // Must be at least Manager to remove users
  const currentRole = currentUserOrgData.role;
  if (compareRoles(currentRole, ROLES.MANAGER) < 0) {
    return false;
  }

  // Cannot remove users with equal or higher role
  if (compareRoles(targetUserOrgData.role, currentRole) >= 0) {
    return false;
  }

  return true;
};

/**
 * Get display name for a role
 * @param {string} roleId - Role identifier
 * @returns {string} Display name
 */
export const getRoleDisplayName = (roleId) => {
  const displayNames = {
    [ROLES.VIEWER]: 'Viewer',
    [ROLES.EDITOR]: 'Editor',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.ADMIN]: 'Admin',
    [ROLES.PRIMARY]: 'Primary'
  };
  return displayNames[roleId] || roleId;
};

/**
 * Get description for a role
 * @param {string} roleId - Role identifier
 * @returns {string} Description
 */
export const getRoleDescription = (roleId) => {
  const descriptions = {
    [ROLES.VIEWER]: 'Can view analytics',
    [ROLES.EDITOR]: 'Can view analytics and edit QR codes',
    [ROLES.MANAGER]: 'Can manage users and their roles',
    [ROLES.ADMIN]: 'Can access billing and organization settings',
    [ROLES.PRIMARY]: 'Account owner with full privileges'
  };
  return descriptions[roleId] || '';
};

/**
 * Get list of roles that a user can assign to others
 * Note: Admin role is not assignable via this UI and must be promoted via organization settings
 * @param {Object} userOrgData - Current user's org data
 * @returns {Array<string>} Array of role IDs that can be assigned
 */
export const getAssignableRoles = (userOrgData) => {
  if (!userOrgData) {
    return [];
  }

  // Primary can assign Viewer, Editor, and Manager roles (not Admin or Primary)
  if (userOrgData.is_primary) {
    return [ROLES.VIEWER, ROLES.EDITOR, ROLES.MANAGER];
  }

  // Others can only assign roles lower than their own (excluding Admin)
  const currentRole = userOrgData.role;
  const currentIndex = ROLE_HIERARCHY.indexOf(currentRole);
  
  if (currentIndex === -1) {
    return [];
  }

  // Get all roles up to and including current role, then filter out Admin
  const assignableRoles = ROLE_HIERARCHY.slice(0, currentIndex + 1);
  return assignableRoles.filter(role => role !== ROLES.ADMIN);
};
